import { guardrailBroken, targetVerdict } from '../model/contract.js';
import { toGuardrails, toTarget } from '../model/eventModel.js';

export const DAY = 86400000;
export const iso = (ms) => new Date(ms).toISOString();
const daysAgo = (n) => iso(Date.now() - n * DAY);
const localDay = (offsetDays, time) => {
    const d = new Date(Date.now() + offsetDays * DAY);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${time}`;
};

export const METRICS = [
    { metricId: 'revenue', metricName: 'Revenue' },
    { metricId: 'dau', metricName: 'DAU' },
    { metricId: 'arpdau', metricName: 'ARPDAU' },
    { metricId: 'sessions', metricName: 'Sessions / player' },
    { metricId: 'd1_retention', metricName: 'D1 retention' },
    { metricId: 'conversion', metricName: 'Payer conversion' },
];

const links = (...ids) => ids.map((configFileId) => ({ configFileId, parentBranchId: null }));

export const newEvent = (id, fields) => ({
    id, featureId: null, aiSuggested: false, recurrence: { end: { type: 'NEVER' } }, configLinks: [],
    target: null, guardrails: [], changes: [], rules: [], segmentIds: [], runMode: null,
    tzMode: 'FIXED', timezone: 'UTC', durationSeconds: null, createdAt: daysAgo(40), updatedAt: daysAgo(2), ...fields,
});

const pctOf = (baseline, actual, supplied) => {
    if (supplied != null) return supplied;
    if (baseline == null || actual == null || baseline === 0) return null;
    return ((actual - baseline) / Math.abs(baseline)) * 100;
};

// As the server builds an outcome: the % change typed in or worked out from before and reached, the verdict
// against the event's goal, and the figures it replaces kept as a revision.
export const scoreOutcome = (event, input, previous = null) => {
    const target = toTarget(event.target);
    const baseline = input?.baseline ?? null;
    const actual = input?.actual ?? null;
    const deltaPct = pctOf(baseline, actual, input?.deltaPct ?? null);
    const noData = !!input?.noData || (deltaPct == null && actual == null);
    const revision = previous && {
        baseline: previous.baseline ?? null, actual: previous.actual ?? null, deltaPct: previous.deltaPct ?? null,
        verdict: previous.verdict, note: previous.note ?? null, recordedAt: previous.recordedAt,
    };
    return {
        targetSnapshot: event.target || null,
        baseline,
        actual,
        deltaPct,
        verdict: noData ? (target ? 'INCONCLUSIVE' : 'NOT_SCORED') : targetVerdict(target, deltaPct, actual),
        inconclusiveReason: noData && target ? 'NO_DATA' : null,
        guardrails: toGuardrails(event.guardrails).map((g, i) => {
            const entered = (input?.guardrails || []).find((x) => x.metricId === g.metricKey);
            const pct = entered ? pctOf(entered.baseline ?? null, entered.actual ?? null, entered.deltaPct ?? null) : null;
            return { guardrail: event.guardrails[i], deltaPct: pct, broken: guardrailBroken(g, pct) };
        }),
        source: 'MANUAL',
        note: input?.note?.trim() || null,
        recordedAt: iso(Date.now()),
        revisions: revision ? [revision, ...(previous.revisions || [])].slice(0, 20) : [],
    };
};

const lastMondays = (count) => {
    const now = new Date();
    const monday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - ((now.getUTCDay() + 6) % 7), 9);
    return Array.from({ length: count + 1 }, (_, i) => monday - (i + 1) * 7 * DAY);
};

const storeRefresh = newEvent('pg-le-002', {
    name: 'Weekly store refresh', description: 'New featured bundles and prices every Monday.', category: 'ECONOMY',
    owner: 'GEOPLAY', sortOrder: 1, scheduleKind: 'RECURRING', intervalValue: 1, intervalUnit: 'WEEK',
    startLocal: localDay(-60, '10:00'), timezone: 'Europe/London', durationSeconds: 604800,
    recurrence: { byWeekday: ['MO'], end: { type: 'NEVER' } }, configLinks: links('pg-cfg-001', 'pg-cfg-002'),
    target: { metricId: 'revenue', direction: 'UP', mode: 'PERCENT', percent: 8, value: null, holdTolerancePct: null },
    guardrails: [{ id: 'pg-gr-001', metricId: 'd1_retention', direction: 'MUST_NOT_FALL', tolerancePct: 1 }],
    changes: [{ id: 'pg-chg-001', configFileId: 'pg-cfg-001', path: 'enabled', defaultValue: false, note: null }],
});

const lifts = [null, 3.1, 11.2, -1.8, 7.9, 6.2];
const retention = [null, -0.2, -1.6, 0.3, 0.1, -0.4];
const mondays = lastMondays(8);

// One run came back without an answer: an experiment was changing the same config.
const overlapped = (outcome) => ({
    ...outcome, verdict: 'INCONCLUSIVE', inconclusiveReason: 'OVERLAP', inconclusiveDetail: 'experiment branch "Gem pack price test"',
});

const instances = [
    ...mondays.slice(0, 6).map((ms, i) => ({
        id: `pg-inst-00${i + 1}`, beatId: 'pg-le-002', occStart: iso(ms), windowEndsAt: iso(ms + 7 * DAY),
        publishedAt: iso(ms - 3600000), status: 'ENDED', skipReason: null,
        shipped: [
            { configFileId: 'pg-cfg-001', path: 'enabled', value: true, removed: false },
            { configFileId: 'pg-cfg-001', path: 'featured.bundleId', value: `bundle_${20 + i}`, removed: false },
        ],
        outcome: i === 0 ? null : (i === 4 ? overlapped : (o) => o)(scoreOutcome(storeRefresh, {
            deltaPct: lifts[i],
            guardrails: [{ metricId: 'd1_retention', deltaPct: retention[i] }],
            note: i === 3 ? 'Featured bundle was a repeat; revenue dipped.' : null,
        })),
    })),
    {
        id: 'pg-inst-007', beatId: 'pg-le-002', occStart: iso(mondays[6]), windowEndsAt: iso(mondays[6] + 7 * DAY),
        publishedAt: null, status: 'SKIPPED', skipReason: 'Store freeze for the launch week', shipped: [], outcome: null,
    },
    {
        id: 'pg-inst-008', beatId: 'pg-le-002', occStart: iso(mondays[7]), windowEndsAt: iso(mondays[7] + 7 * DAY),
        publishedAt: null, status: 'MISSED', skipReason: null, shipped: [], outcome: null,
    },
    {
        id: 'pg-inst-009', beatId: 'pg-le-002', occStart: iso(mondays[8]), windowEndsAt: iso(mondays[8] + 7 * DAY),
        publishedAt: iso(mondays[8] - 7200000), status: 'CANCELLED', skipReason: null, shipped: [], outcome: null,
    },
];

// As the server keeps it: every date that published gets a run record for its notes and files.
const dateRecords = instances.filter((i) => i.publishedAt).map((i, n) => ({
    id: `pg-run-date-${n + 1}`, liveEventId: i.beatId, instanceId: i.id, ranAt: i.occStart, title: null,
    notes: i.id === 'pg-inst-002' ? 'Featured bundle_21 and cut the gem pack price 10%.' : null,
    results: i.id === 'pg-inst-002' ? 'Store CVR 6.4%, revenue up week on week.' : null,
    createdAt: i.publishedAt, updatedAt: i.publishedAt,
}));

export const seed = () => ({
    seq: 100,
    events: [
        newEvent('pg-le-001', {
            name: 'Daily login bonus', description: 'A reward for the first login of each day.', category: 'LIVEOPS',
            owner: 'GEOPLAY', sortOrder: 0, scheduleKind: 'RECURRING', intervalValue: 1, intervalUnit: 'DAY',
            startLocal: localDay(-30, '09:00'), tzMode: 'FLOATING', timezone: null, durationSeconds: 86400,
            configLinks: links('pg-cfg-006'),
        }),
        storeRefresh,
        newEvent('pg-le-003', {
            name: 'Weekend XP boost', description: 'Double XP across every mode from Saturday to Monday.', category: 'META',
            owner: 'SHARED', sortOrder: 2, scheduleKind: 'RECURRING', intervalValue: 1, intervalUnit: 'WEEK',
            startLocal: localDay(-20, '00:00'), durationSeconds: 172800,
            recurrence: { byWeekday: ['SA'], end: { type: 'NEVER' } }, configLinks: links('pg-cfg-004', 'pg-cfg-005'),
        }),
        newEvent('pg-le-004', {
            name: 'Ranked season reset', description: 'Resets ranks and grants season rewards.', category: 'COMPETITIVE',
            owner: 'GEOPLAY', sortOrder: 3, scheduleKind: 'RECURRING', intervalValue: 1, intervalUnit: 'MONTH',
            startLocal: localDay(-45, '00:00'), recurrence: { monthly: { mode: 'DAY_OF_MONTH', day: 1 }, end: { type: 'NEVER' } },
            configLinks: links('pg-cfg-011'),
        }),
        newEvent('pg-le-005', {
            name: 'Diwali festival', description: 'Festive bundles and a lantern hunt for India.', category: 'LIVEOPS',
            owner: 'GEOPLAY', sortOrder: 4, scheduleKind: 'ONE_TIME', intervalValue: 1, intervalUnit: 'DAY',
            startLocal: localDay(40, '00:00'), timezone: 'Asia/Kolkata', durationSeconds: 432000,
        }),
        newEvent('pg-le-006', {
            name: 'Server maintenance window', description: 'Planned downtime run by the game server team.', category: 'TECHNICAL',
            owner: 'GAME_SERVER', sortOrder: 5, scheduleKind: 'RECURRING', intervalValue: 2, intervalUnit: 'WEEK',
            startLocal: localDay(3, '02:00'), durationSeconds: 7200,
        }),
    ],
    placements: ['pg-le-001', 'pg-le-002', 'pg-le-003', 'pg-le-004'].map((beatId, i) => ({
        id: `pg-tl-00${i + 1}`, flywheelId: 'pg-fw-001', laneId: 'pg-lane-001', kind: 'BEAT', beatId,
        startAt: daysAgo(30), horizonDays: null, createdAt: daysAgo(30), updatedAt: daysAgo(30),
    })),
    instances,
    taskLists: [{
        id: 'pg-tl-list-001', beatId: 'pg-le-002', name: 'Weekly store refresh', isDefault: true, sortOrder: 0,
        createdAt: daysAgo(20), updatedAt: daysAgo(3),
        items: [
            { id: 'pg-task-001', title: 'Turn off the enabled flag for the old featured bundle', description: null, sortOrder: 0, actor: 'AI', required: true, links: [{ targetType: 'CONFIG', targetId: 'pg-cfg-001' }] },
            { id: 'pg-task-002', title: 'Make the weekend bundle 20% cheaper', description: 'Premium tiers only.', sortOrder: 1, actor: 'AI', required: true, links: [] },
            { id: 'pg-task-003', title: 'Refresh the store banner art', description: null, sortOrder: 2, actor: 'AI', required: true, links: [] },
        ],
    }],
    snippets: [
        { id: 'pg-snip-001', system: 'LIVE_EVENT', systemId: 'pg-le-002', kind: 'MONETIZATION', title: 'Bundles refresh every Monday at 10:00 UK time', body: 'The store rotates on Monday morning, London time, so weekend buyers see the old set.', source: 'MANUAL', suggestedAction: 'NONE', createdAt: daysAgo(12) },
        { id: 'pg-snip-002', system: 'LIVE_EVENT', systemId: 'pg-le-002', kind: 'LIVEOPS', title: 'The featured slot always holds the newest bundle', body: 'Never feature a bundle that was already featured in the last four weeks.', source: 'CHAT', suggestedAction: 'NONE', createdAt: daysAgo(10) },
        { id: 'pg-snip-003', system: 'LIVE_EVENT', systemId: 'pg-le-001', kind: 'PROGRESSION', title: 'Day 7 reward is always a premium chest', body: 'Streak day seven grants the premium chest; days one to six grant soft currency.', source: 'MANUAL', suggestedAction: 'NONE', createdAt: daysAgo(8) },
    ],
    runs: [
        { id: 'pg-run-001', liveEventId: 'pg-le-002', ranAt: daysAgo(70), title: 'Week 28 store refresh', notes: 'Swapped the featured bundle and raised gem pack prices 5%.', results: 'Store CVR 6.1%, ARPDAU +$0.03.', createdAt: daysAgo(69), updatedAt: daysAgo(69) },
        ...dateRecords,
    ],
    runArtifacts: [
        { id: 'pg-ra-001', liveEventRunId: 'pg-run-001', role: 'INPUT', name: 'store-offers.json', mimeType: null, size: 0, sourceUrl: null, configFileId: 'pg-cfg-001', configBranchId: null, configVersion: null, createdAt: daysAgo(69) },
        { id: 'pg-ra-002', liveEventRunId: 'pg-run-001', role: 'RESULT', name: 'Results dashboard', mimeType: null, size: 0, sourceUrl: 'https://lookerstudio.google.com/', createdAt: daysAgo(69) },
    ],
    documents: [],
    testRuns: {},
});
